/**
 * Accounts (ADR-0031): invitations, password reset, e-mail verification, two-step sign-in,
 * staff invitations and the onboarding wizard. Keys are flat, dot-namespaced and stable.
 */
export const enAccount = {
  // ---- Sign-in additions --------------------------------------------------------------
  'auth.signIn.forgot': 'Forgot password?',
  'auth.signIn.totp': 'Authenticator code',
  'auth.signIn.totpHint': 'Open your authenticator app and enter the 6-digit code.',
  'auth.signIn.error.totpRequired': 'Enter the 6-digit code from your authenticator app.',
  'auth.signIn.error.totpInvalid': 'The code is incorrect or has expired. Try the current code.',

  // ---- Shared ---------------------------------------------------------------------------
  'account.backToSignIn': 'Back to sign-in',
  'account.password': 'Password',
  'account.passwordHint': 'At least 10 characters.',
  'account.passwordRepeat': 'Repeat password',
  'account.error.passwordTooShort': 'Use at least 10 characters.',
  'account.error.passwordTooLong': 'Use at most 256 characters.',
  'account.error.passwordMismatch': 'The passwords do not match.',
  'account.error.nameRequired': 'Enter your name.',
  'account.error.termsRequired': 'Accept the terms of service to continue.',
  'account.error.emailRequired': 'Enter your e-mail address.',
  'account.error.emailInvalid': 'Enter a valid e-mail address.',

  // ---- Accept invitation ----------------------------------------------------------------
  'account.invite.title': 'Join {tenant}',
  'account.invite.subtitle': 'You were invited as {email}. Choose a password to sign in.',
  'account.invite.name': 'Your name',
  'account.invite.terms': 'I agree to the terms of service (version {version}).',
  'account.invite.termsUnversioned': 'I agree to the terms of service.',
  'account.invite.termsLink': 'Read the terms of service',
  'account.invite.expires': 'This invitation is valid until {date}.',
  'account.invite.submit': 'Join and sign in',
  'account.invite.submitting': 'Joining…',
  'account.invite.invalidTitle': 'This invitation can no longer be used',
  'account.invite.invalidText':
    'The link has expired or was already used. Ask the person who invited you to send a new invitation.',

  // ---- Password reset --------------------------------------------------------------------
  'account.reset.requestTitle': 'Reset your password',
  'account.reset.requestSubtitle':
    'Enter the e-mail address of your account. We will send a link to choose a new password.',
  'account.reset.email': 'E-mail',
  'account.reset.requestSubmit': 'Send reset link',
  'account.reset.requestSubmitting': 'Sending…',
  'account.reset.sentTitle': 'Check your e-mail',
  'account.reset.sentText':
    'If {email} belongs to an account, a reset link is on its way. The link is valid for one hour.',
  'account.reset.setTitle': 'Choose a new password',
  'account.reset.setSubtitle':
    'After saving, every device signed in to your account is signed out.',
  'account.reset.newPassword': 'New password',
  'account.reset.setSubmit': 'Save new password',
  'account.reset.setSubmitting': 'Saving…',
  'account.reset.doneTitle': 'Password changed',
  'account.reset.doneText': 'Sign in with your new password.',
  'account.reset.invalidTitle': 'This link can no longer be used',
  'account.reset.invalidText':
    'Reset links are valid for one hour and work once. Request a new link to try again.',
  'account.reset.requestNew': 'Request a new link',

  // ---- E-mail verification ---------------------------------------------------------------
  'account.verify.pendingTitle': 'Confirming your e-mail…',
  'account.verify.doneTitle': 'E-mail confirmed',
  'account.verify.doneText': 'Thank you — your e-mail address is confirmed.',
  'account.verify.invalidTitle': 'This link can no longer be used',
  'account.verify.invalidText':
    'The link has expired or was already used. Send a new confirmation link from your account page.',
  'account.verify.continue': 'Continue',

  // ---- Terms of service placeholder (ADR-0030 D7: documents are a legal deliverable)
  'account.terms.title': 'Terms of service',
  'account.terms.placeholder':
    'The terms of service will be published on this page. Until then, ask your apis contact for the current version.',

  // ---- Back office: account & security ----------------------------------------------------
  'bo.nav.account': 'Account & security',
  'bo.account.title': 'Account & security',
  'bo.account.subtitle': 'How you sign in to {email}.',
  'bo.account.emailTitle': 'E-mail address',
  'bo.account.emailDesc': 'Used to sign in and to reset your password.',
  'bo.account.emailVerified': 'Verified',
  'bo.account.emailUnverified': 'Unverified',
  'bo.account.emailSend': 'Send confirmation link',
  'bo.account.emailSending': 'Sending…',
  'bo.account.emailSent': 'Confirmation link sent to {email}.',
  'bo.account.termsAccepted': 'Terms of service accepted on {date} (version {version}).',
  'bo.account.mfaTitle': 'Two-step sign-in',
  'bo.account.mfaDesc':
    'After your password, ask for a 6-digit code from an authenticator app such as Google Authenticator or 1Password.',
  'bo.account.mfaOn': 'On',
  'bo.account.mfaOff': 'Off',
  'bo.account.mfaNotEligible': 'Available to owners, administrators and managers.',
  'bo.account.mfaStart': 'Turn on two-step sign-in',
  'bo.account.mfaStarting': 'Preparing…',
  'bo.account.mfaScan':
    'Scan the QR code with your authenticator app, or type the setup key, then enter the code it shows.',
  'bo.account.mfaQrLabel': 'QR code for your authenticator app',
  'bo.account.mfaKey': 'Setup key',
  'bo.account.mfaCode': '6-digit code',
  'bo.account.mfaCodeInvalid': 'Enter the 6 digits shown in the app.',
  'bo.account.mfaCodeWrong': 'The code is incorrect or has expired.',
  'bo.account.mfaConfirm': 'Confirm and turn on',
  'bo.account.mfaConfirming': 'Confirming…',
  'bo.account.mfaEnabled': 'Two-step sign-in is on.',
  'bo.account.mfaDisableHint': 'Enter a current code from your app to turn two-step sign-in off.',
  'bo.account.mfaDisable': 'Turn off',
  'bo.account.mfaDisabling': 'Turning off…',
  'bo.account.mfaDisabled': 'Two-step sign-in is off.',

  // ---- Staff invitations ------------------------------------------------------------------
  'bo.staff.invite.formDesc':
    'Leave the password empty to invite by e-mail: they choose their own password.',
  'bo.staff.invite.password': 'Initial password (optional)',
  'bo.staff.invite.passwordHint':
    'Empty = send an invitation. At least 10 characters otherwise. Ignored if the e-mail already has an account.',
  'bo.staff.invite.submitInvite': 'Send invitation',
  'bo.staff.invite.sent': 'Invitation for {name} created.',
  'bo.staff.invite.linkTitle': 'Invitation link for {name}',
  'bo.staff.invite.linkDesc':
    'E-mail delivery is not active yet. Send this link yourself (for example by WhatsApp). It is valid for 7 days and works once.',
  'bo.staff.invite.linkLabel': 'Invitation link',
  'bo.staff.invite.copy': 'Copy link',
  'bo.staff.invite.copied': 'Link copied.',
  'bo.staff.invite.done': 'Done',

  // ---- Onboarding wizard --------------------------------------------------------------------
  'bo.onboarding.title': 'Set up your restaurant',
  'bo.onboarding.subtitle': 'Six short steps from an empty account to the first order.',
  'bo.onboarding.promptTitle': 'Finish setting up {branch}',
  'bo.onboarding.promptDesc':
    'Add tables and menu items so the cashier and the kitchen can take orders.',
  'bo.onboarding.promptAction': 'Continue setup',
  'bo.onboarding.stepOf': 'Step {n} of {total}',
  'bo.onboarding.stepsLabel': 'Setup steps',
  'bo.onboarding.statusDone': 'Done',
  'bo.onboarding.statusTodo': 'To do',
  'bo.onboarding.next': 'Next',
  'bo.onboarding.back': 'Back',
  'bo.onboarding.finish': 'Go to dashboard',
  'bo.onboarding.step.branch': 'Branch details',
  'bo.onboarding.step.tax': 'Tax & service',
  'bo.onboarding.step.tables': 'Floors & tables',
  'bo.onboarding.step.menu': 'Menu',
  'bo.onboarding.step.staff': 'Invite staff',
  'bo.onboarding.step.qr': 'Table QR codes',
  'bo.onboarding.desc.branch':
    'Check the branch name, time zone and currency. They appear on receipts and reports.',
  'bo.onboarding.desc.tax':
    'Restaurant tax (PB1) and service charge are added to every bill of this branch.',
  'bo.onboarding.desc.tables':
    'Create the floors of the restaurant and the tables on them. Orders are taken per table.',
  'bo.onboarding.desc.menu':
    'Add categories and menu items with prices. The kitchen station decides where tickets print.',
  'bo.onboarding.desc.staff':
    'Invite cashiers, waiters and the kitchen. Each person signs in with their own account.',
  'bo.onboarding.desc.qr':
    'Print a QR code for every table so guests can open the menu and the bill on their phone.',
  'bo.onboarding.info.branch': '{name} · {code} · {timeZone} · {currency}',
  'bo.onboarding.info.tax': 'Restaurant tax {tax} · service charge {service}',
  'bo.onboarding.info.tables': '{floors} floors · {tables} tables',
  'bo.onboarding.info.menu': '{count} menu items',
  'bo.onboarding.info.staff': '{count} people',
  'bo.onboarding.info.qr': '{count} tables to print',
  'bo.onboarding.menuCsvNote':
    'Importing a menu from a CSV file is not available yet — add items on the menu page.',
  'bo.onboarding.open.branch': 'Open branch settings',
  'bo.onboarding.open.tables': 'Open floors & tables',
  'bo.onboarding.open.menu': 'Open menu',
  'bo.onboarding.open.staff': 'Open staff',
  'bo.onboarding.open.qr': 'Open QR codes',
  'bo.onboarding.ownerOnly': 'Only the owner can run the setup guide.',
  'auth.google.continue': 'Continue with Google',
  'auth.google.or': 'or',
  'auth.google.signUpHint':
    'New here? Continue with Google to start a free trial for your restaurant.',
  'auth.google.inviteHint': 'Use the Google account for {email}. Tick the terms box first.',
  'auth.google.mfaTitle': 'Authenticator code',
  'auth.google.mfaText':
    'Google confirmed your account. Enter the 6-digit code from your authenticator app to finish signing in.',
  'auth.google.error.cancelled': 'Google sign-in was cancelled.',
  'auth.google.error.state':
    'This sign-in attempt expired or was opened in another browser. Start again.',
  'auth.google.error.unverified':
    'Google has not verified this e-mail address, so it cannot be used here.',
  'auth.google.error.disabled': 'This account is disabled. Contact your manager.',
  'auth.google.error.inviteMismatch':
    'That Google account does not match the invited e-mail address.',
  'auth.google.error.inviteInvalid': 'This invitation is no longer valid. Ask for a new one.',
  'auth.google.error.otherAccount': 'Your account is already linked to a different Google account.',
  'auth.google.error.failed': 'Google sign-in did not work. Try again or use your password.',
  'signUp.title': 'Start your free trial',
  'signUp.subtitle':
    'Signed in with Google as {email}. Your restaurant gets {days} days free; no card needed.',
  'signUp.name': 'Your name',
  'signUp.business': 'Restaurant name',
  'signUp.businessHint': 'Shown on receipts and the guest menu; you can change it later.',
  'signUp.branch': 'First outlet name',
  'signUp.branchHint': 'Leave empty to use the restaurant name.',
  'signUp.timeZone': 'Time zone',
  'signUp.tz.jakarta': 'WIB — Jakarta, Sumatra, Java, West & Central Kalimantan',
  'signUp.tz.makassar': 'WITA — Bali, Nusa Tenggara, Sulawesi, East Kalimantan',
  'signUp.tz.jayapura': 'WIT — Maluku, Papua',
  'signUp.submit': 'Create my restaurant',
  'signUp.submitting': 'Creating…',
  'signUp.error.required': 'Enter your name and the restaurant name.',
  'signUp.expiredTitle': 'Start again with Google',
  'signUp.expiredText': 'The sign-up session has expired or was opened in another browser.',
} as const;
